// --- React
import React, { useState } from "react";
// --- External UI Libraries
import Pillbox from "@dtci/spellbook/Pillbox";
// --- Components
import { PillboxLabelContent } from "./PillboxLabelContent.jsx";
// --- Utils
import {
  getPillboxExpansionKey,
  isPillboxExpanded,
  setPillboxExpanded,
} from "tap/utils/pillboxExpansionState.js";

const ROW_GAP_PX = 8;

/**
 * ExpandablePillboxList - A cell renderer that displays items as stacked pillboxes
 * with expand/collapse functionality.
 *
 * Features:
 * - Shows first N items (default 5) in rows of 2 with dynamic width
 * - Displays +X/-  pillbox toggle at the end of the last row
 * - Triggers AG Grid row height recalculation on expand/collapse
 *
 * @param {Object} props - AG Grid cell renderer props (via cellRendererParams)
 * @param {Array} props.value - Array of items to display
 * @param {string} [props.labelField] - Field name to use for item label
 * @param {number} [props.maxItems=5] - Maximum items to show when collapsed
 * @param {number} [props.itemsPerRow=2] - Number of items per row
 * @param {number} [props.maxExpandedHeight=300] - Max height in px when expanded
 * @param {boolean} [props.preserveTrailingId] - Keep trailing (id) visible when truncating
 * @param {boolean} [props.truncateLabels] - Constrain pill width and ellipsis long labels
 * @param {Object} [props.data] - AG Grid row data (for stable expansion key)
 * @param {string} [props.expansionKeyPrefix] - Persists expanded state across grid refresh
 * @returns {JSX.Element|null}
 */
export const ExpandablePillboxList = ({
  value,
  labelField,
  maxItems: maxItemsProp,
  itemsPerRow: itemsPerRowProp,
  maxExpandedHeight: maxExpandedHeightProp,
  preserveTrailingId = false,
  truncateLabels = false,
  data,
  expansionKeyPrefix,
  color = "primary",
}) => {
  const expansionKey = getPillboxExpansionKey(expansionKeyPrefix, data?.id);

  const [isExpanded, setIsExpanded] = useState(() =>
    expansionKey ? isPillboxExpanded(expansionKey) : false,
  );

  // Get the display label for an item
  const getItemLabel = (item) => {
    if (typeof item === "string") {
      return item;
    }
    if (labelField && item[labelField]) {
      return item[labelField];
    }
    // Fallback to common label fields
    return item.titleWithId || item.name || item.label || item.id || "";
  };

  // Filter out items with empty labels
  const filteredValue = Array.isArray(value)
    ? value.filter((item) => {
        const label = getItemLabel(item);
        return label && label.toString().trim() !== "";
      })
    : [];

  if (filteredValue.length === 0) {
    return null;
  }

  const maxToShow = maxItemsProp || 5;
  const itemsPerRow = itemsPerRowProp || 2;
  // Never render a "+1" collapse pill — if only one item would be hidden, just
  // show it inline. Collapsing saves space only when it hides ≥ 2 items.
  const hasMore = filteredValue.length - maxToShow >= 2;
  const visibleItems =
    isExpanded || !hasMore ? filteredValue : filteredValue.slice(0, maxToShow);
  const remainingCount = filteredValue.length - maxToShow;

  const maxPillWidthForRowCount = (countInRow) =>
    truncateLabels && countInRow > 0
      ? `calc((100% - ${(countInRow - 1) * ROW_GAP_PX}px) / ${countInRow})`
      : undefined;

  // Group items into rows
  const rows = Array.from(
    { length: Math.ceil(visibleItems.length / itemsPerRow) },
    (_, i) =>
      visibleItems.slice(i * itemsPerRow, i * itemsPerRow + itemsPerRow),
  );

  const isLastRow = (rowIdx) => rowIdx === rows.length - 1;

  const toggleExpand = (e) => {
    e.stopPropagation();
    const next = !isExpanded;
    setIsExpanded(next);
    if (expansionKey) {
      setPillboxExpanded(expansionKey, next);
    }
  };

  // Max height for expanded state to prevent excessive row growth
  const maxExpandedHeight = maxExpandedHeightProp || 300;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: `${ROW_GAP_PX}px`,
        padding: "8px 0",
        width: "100%",
        ...(isExpanded && {
          maxHeight: maxExpandedHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }),
      }}
    >
      {rows.map((rowItems, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: "flex",
            gap: `${ROW_GAP_PX}px`,
            flexWrap: truncateLabels ? "nowrap" : "wrap",
            alignItems: "center",
            width: truncateLabels ? "100%" : undefined,
          }}
        >
          {rowItems.map((item, idx) => {
            const label = getItemLabel(item);
            const maxPillWidth = maxPillWidthForRowCount(rowItems.length);
            const pill = (
              <Pillbox
                badgeRole="listitem"
                color={color}
                light
                showClose={false}
                size="small"
                style={
                  truncateLabels
                    ? { maxWidth: "100%", overflow: "hidden" }
                    : undefined
                }
              >
                {truncateLabels ? (
                  <PillboxLabelContent
                    label={label}
                    item={item}
                    preserveTrailingId={preserveTrailingId}
                    truncate
                  />
                ) : (
                  label
                )}
              </Pillbox>
            );

            if (!truncateLabels) {
              return (
                <React.Fragment key={item.id || `${rowIdx}-${idx}`}>
                  {pill}
                </React.Fragment>
              );
            }

            return (
              <div
                key={item.id || `${rowIdx}-${idx}`}
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  maxWidth: maxPillWidth,
                  overflow: "hidden",
                }}
              >
                {pill}
              </div>
            );
          })}
          {hasMore && isLastRow(rowIdx) && (
            <Pillbox
              badgeRole="button"
              color={color}
              light
              showClose={false}
              size="small"
              onClick={toggleExpand}
              style={{ cursor: "pointer", flexShrink: 0 }}
            >
              {isExpanded ? "−" : `+${remainingCount}`}
            </Pillbox>
          )}
        </div>
      ))}
    </div>
  );
};
